import { createNode } from '@glueapp/graph-types';

export const ForEachItemInList = createNode({
    id: 'For each Item in List',
    name: 'For each Item in List',
    description:
        'Execute a function for each item in the list. The function receives each item, can process it and the functions output is used to build a new output list.',
    aiDescription: [
        'Maps over a list by executing a function for each item (like Array.map). Async — uses executeFunctionNode.',
        'Use this to loop / iterate / map over a list (keywords: loop, iterate, iterator, foreach, for each, map).',
        'Setup steps:',
        '1. Create a function with inputs "Item" (type T), "Index" (number), "List" (array of T) and output "Result" (type R).',
        '   Inside the function, transform the item and output the transformed value as "Result".',
        '2. Add this node.',
        '3. Connect a list to "Input List".',
        '4. Bind the function to the "Item Processor" port (kind: function) by SETTING its value to a function reference (not a connection): set_value <thisNodeId> "Item Processor" {"isFunctionReferenceValue":true,"functionId":"<functionId>"}.',
        'Outputs: "Result" (array of R, one entry per input item), "Processed Count" (number).',
    ].join('\n'),
    category: 'List',
    color: '#778ed5',
    isAsync: true, // Function execution is async

    inPorts: {
        'Input List': {
            kind: 'array',
            subType: {
                kind: 'generic',
                identifier: 'T',
            },
        },
        'Item Processor': {
            kind: 'function',
            inputTypes: {
                Item: { kind: 'generic', identifier: 'T' },
                Index: { kind: 'number' },
                List: {
                    kind: 'array',
                    subType: { kind: 'generic', identifier: 'T' },
                },
            },
            outputTypes: {
                Result: { kind: 'generic', identifier: 'R' },
            },
        },
    },

    outPorts: {
        Result: {
            kind: 'array',
            subType: {
                kind: 'generic',
                identifier: 'R',
            },
        },
        'Processed Count': {
            kind: 'number',
        },
    },

    execute: async ({ inPorts, executeFunctionNode }) => {
        if (!executeFunctionNode) {
            throw new Error('Function execution not available');
        }

        const list = inPorts['Input List'] as any[];
        const itemProcessorValue = inPorts['Item Processor'];

        // Check if the processor is a FunctionReferenceValue
        if (
            !itemProcessorValue ||
            typeof itemProcessorValue !== 'object' ||
            !('isFunctionReferenceValue' in itemProcessorValue) ||
            !itemProcessorValue.isFunctionReferenceValue
        ) {
            throw new Error('Item Processor must be a function reference');
        }

        const itemProcessor = itemProcessorValue as any; // Type as FunctionReferenceValue

        if (!list || !Array.isArray(list)) {
            return {
                output: {
                    Result: [],
                    'Processed Count': 0,
                },
            };
        }

        const results = [];

        for (let i = 0; i < list.length; i++) {
            const item = list[i];

            // Execute the item processor function using the function ID from the reference
            const result = await executeFunctionNode(itemProcessor.functionId, {
                Item: item,
                Index: i,
                List: list,
            });

            results.push(result.output.Result);
        }

        return {
            output: {
                Result: results,
                'Processed Count': results.length,
            },
        };
    },
});
